import { useLayoutEffect, useRef, type HTMLAttributes, type LabelHTMLAttributes, type ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface AppShellProps {
  sidebar: ReactNode;
  topbar: ReactNode;
  mobileNav: ReactNode;
  overlays?: ReactNode;
  children: ReactNode;
  className?: string;
  contentFocusKey?: string;
  contentLabel?: string;
}

export function AppShell({
  sidebar,
  topbar,
  mobileNav,
  overlays,
  children,
  className,
  contentFocusKey,
  contentLabel = "Workspace content"
}: AppShellProps) {
  const mainRef = useRef<HTMLElement>(null);
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const previousContentFocusKeyRef = useRef(contentFocusKey);

  useLayoutEffect(() => {
    if (contentFocusKey === undefined) {
      previousContentFocusKeyRef.current = undefined;
      return;
    }
    if (previousContentFocusKeyRef.current === contentFocusKey) {
      return;
    }

    previousContentFocusKeyRef.current = contentFocusKey;
    mainScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    mainRef.current?.focus({ preventScroll: true });
  }, [contentFocusKey]);

  return (
    <div className={cn("ui-app-shell", className)}>
      <a className="ui-skip-link" href="#workspace-main">
        Skip to workspace content
      </a>
      {sidebar}
      <div className="ui-app-main" ref={mainScrollRef}>
        {topbar}
        <main id="workspace-main" className="ui-screen-shell" tabIndex={-1} aria-label={contentLabel} ref={mainRef}>
          {children}
        </main>
      </div>
      {mobileNav}
      {overlays}
    </div>
  );
}

type AppShellWorkspaceContextProps =
  | ({ as?: "label" } & LabelHTMLAttributes<HTMLLabelElement>)
  | ({ as: "span" } & HTMLAttributes<HTMLSpanElement>);

export function AppShellWorkspaceContext({ as = "label", className, ...props }: AppShellWorkspaceContextProps) {
  const classes = cn("ui-app-workspace-context", as === "span" && "ui-app-workspace-context-static", className);

  if (as === "span") {
    return <span className={classes} {...(props as HTMLAttributes<HTMLSpanElement>)} />;
  }

  return <label className={classes} {...(props as LabelHTMLAttributes<HTMLLabelElement>)} />;
}
